"use client";

import React, { useState } from "react";
import { UserCheck, Target, PoundSterling, Zap, ShieldCheck, Sparkles, ArrowRight, CircleCheck as CheckCircle2, CircleX as XCircle, MessageSquareQuote } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const benefits = [{
  id: "direct-access",
  title: "100% Direct Consultant Access",
  subtitle: "No account managers or junior delegates",
  consultantPoint: "You work directly with me, Yassine Essaadi. Every audit, strategy session, and technical update is personally designed and executed by senior expertise.",
  agencyPoint: "Your account is frequently passed to junior coordinators while senior strategists only appear during initial sales presentations.",
  blushHighlight: "Direct Senior Expertise",
  iconName: "UserCheck"
}, {
  id: "custom-strategy",
  title: "Bespoke & Tailored SEO Strategy",
  subtitle: "Built specifically for your market and unit economics",
  consultantPoint: "I build a custom SEO roadmap aligned strictly with your platform, business model, and revenue goals—never generic automated task lists.",
  agencyPoint: "Standardized agency templates, rigid deliverable scopes, and recurring monthly checklists that ignore your unique technical challenges.",
  blushHighlight: "Zero Cookie-Cutter Templates",
  iconName: "Target"
}, {
  id: "cost-efficiency",
  title: "Maximum ROI & Overhead Efficiency",
  subtitle: "Every pound invested drives direct search performance",
  consultantPoint: "You pay strictly for strategic expertise and execution, not expensive office spaces, bloated executive teams, or account management layers.",
  agencyPoint: "Up to 50% of your monthly retainer pays for agency overheads, sales commissions, and internal administrative meetings.",
  blushHighlight: "Pure Strategic Value",
  iconName: "PoundSterling"
}, {
  id: "agility-speed",
  title: "Rapid Execution & High Agility",
  subtitle: "Instant pivots when search algorithms change",
  consultantPoint: "Direct communication channels mean rapid decision-making, fast technical code fixes, and swift adaptation to Google updates.",
  agencyPoint: "Slow email ticketing systems, layered approval chains, and bureaucratic delays that hold up critical fixes for weeks.",
  blushHighlight: "Immediate Technical Action",
  iconName: "Zap"
}, {
  id: "transparency",
  title: "Honest Reporting & Clear Deliverables",
  subtitle: "Focusing on qualified conversions over vanity metrics",
  consultantPoint: "I measure success through organic leads, bottom-line revenue impact, and clear technical progress that you can actually verify.",
  agencyPoint: "Vague monthly slide decks with confusing traffic charts, keyword rankings for useless queries, and superficial vanity reporting.",
  blushHighlight: "100% Honest Measurement",
  iconName: "ShieldCheck"
}, {
  id: "partnership",
  title: "Dedicated Client Focus",
  subtitle: "A selective client list ensures deep involvement",
  consultantPoint: "I intentionally limit the number of businesses I work with at any one time so I can immerse myself thoroughly into your ecosystem.",
  agencyPoint: "Account managers handling 15+ accounts simultaneously, leading to fragmented attention and superficial strategy.",
  blushHighlight: "Unflinching Dedication",
  iconName: "Sparkles"
}];
export default function ConsultantBenefits() {
  const [activeTab, setActiveTab] = useState<string>("direct-access");
  const shouldReduceMotion = useReducedMotion();
  const activeBenefit = benefits.find(b => b.id === activeTab) || benefits[0];
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "UserCheck":
        return <UserCheck className="size-5 text-primary" />;
      case "Target":
        return <Target className="size-5 text-primary" />;
      case "PoundSterling":
        return <PoundSterling className="size-5 text-primary" />;
      case "Zap":
        return <Zap className="size-5 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-primary" />;
      default:
        return <UserCheck className="size-5 text-primary" />;
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 size-96 bg-accent/15 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-accent/30 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Why Work Directly With Me
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif text-foreground tracking-tight">
            Independent Consultant vs. SEO Agency
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When you hire me, you get my direct expertise. No account managers, no junior delegates, and no inflated agency overheads. Just hands-on, high-impact SEO tailored to your business.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 mb-3">
              Key Advantages
            </h3>
            {benefits.map((benefit, i) => {
            const isActive = benefit.id === activeTab;
            return <div key={benefit.id} data-index={i}>
                  <Button variant="ghost" onClick={() => setActiveTab(benefit.id)} className={`w-full justify-start text-left h-auto py-4 px-4 rounded-xl border transition-all ${isActive ? "bg-card border-primary/40 shadow-md text-foreground" : "bg-background border-border/60 hover:bg-muted/60 text-muted-foreground hover:text-foreground"}`}>
                    <div className="flex items-center gap-3 w-full">
                      <div className={`p-2 rounded-lg shrink-0 ${isActive ? "bg-primary/10" : "bg-muted"}`}>
                        {getIcon(benefit.iconName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm sm:text-base truncate">
                            {benefit.title}
                          </p>
                          <Badge variant="outline" className="bg-accent/15 border-accent/40 text-foreground text-[11px] px-2 py-0.5 shrink-0 hidden sm:inline-flex">
                            {benefit.blushHighlight}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {benefit.subtitle}
                        </p>
                      </div>
                    </div>
                  </Button>
                </div>;
          })}
          </div>

          <div className="lg:col-span-7">
            <motion.div key={activeBenefit.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border-border shadow-lg bg-card overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border pb-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10">
                        {getIcon(activeBenefit.iconName)}
                      </div>
                      <div>
                        <CardTitle className="text-xl sm:text-2xl font-semibold font-serif">
                          {activeBenefit.title}
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm mt-0.5">
                          {activeBenefit.subtitle}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-accent/40 text-xs px-2.5 py-1 font-medium">
                      {activeBenefit.blushHighlight}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-6 space-y-6">
                  <div className="p-5 rounded-xl bg-primary/5 border border-primary/20 space-y-3 relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-5 text-primary shrink-0" />
                        <span className="font-semibold text-sm uppercase tracking-wider text-primary">
                          Working With Me (Yassine Essaadi)
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground font-medium bg-background/80 px-2.5 py-1 rounded-md border border-border">
                        Direct Access
                      </span>
                    </div>
                    <p className="text-sm sm:text-base text-foreground leading-relaxed">
                      "{activeBenefit.consultantPoint}"
                    </p>
                  </div>

                  <div className="p-5 rounded-xl bg-muted/40 border border-border/80 space-y-3">
                    <div className="flex items-center gap-2">
                      <XCircle className="size-5 text-destructive shrink-0" />
                      <span className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                        Traditional SEO Agency Model
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {activeBenefit.agencyPoint}
                    </p>
                  </div>

                  <Separator />

                  <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary/50 border border-accent/30">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-12 rounded-full object-cover border-2 border-primary shrink-0" loading="lazy" />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <MessageSquareQuote className="size-3.5 text-accent" />
                        <span>My Personal Guarantee to You</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        I am personally responsible for your search growth. No excuses, no intermediate delegates, just direct strategy focused on real ROI.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 sm:mt-20 p-8 sm:p-10 rounded-2xl bg-card border border-border shadow-md text-center max-w-4xl mx-auto space-y-6">
          <Badge variant="outline" className="border-accent/40 bg-secondary/30 text-foreground px-3 py-1 text-xs font-medium">
            Ready For Clear, Direct Expertise?
          </Badge>
          
          <h3 className="text-2xl sm:text-3xl font-semibold font-serif text-foreground">
            Let's Discuss Your Sustainable Search Strategy
          </h3>
          
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Book a complimentary SEO consultation directly with me. I'll evaluate your website's search performance and identify high-impact growth opportunities.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}